import { memo } from 'react';
import { TrashIcon } from '@heroicons/react/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';

import useLocale from 'next-translate/useTranslation';
import Spinner from '@/components/Spinner';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface AnnotationToolbarProps {
  markerCount: number;
  hasValidAnnotations: boolean;
  onClearAll: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onCancel: () => void;
  onDone: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isGenerating?: boolean;
  embedded?: boolean;
}

function AnnotationToolbar({
  markerCount,
  hasValidAnnotations,
  onClearAll,
  onUndo,
  onRedo,
  onCancel,
  onDone,
  canUndo,
  canRedo,
  isGenerating,
  embedded = false,
}: AnnotationToolbarProps) {
  const { t } = useLocale('ai-images');

  return (
    <div className='sm:absolute sm:top-4 sm:left-1/2 sm:-translate-x-1/2 z-20 w-full px-2 sm:w-auto sm:px-0'>
      <div className='flex items-center justify-center gap-1 sm:gap-1.5 p-1 sm:p-1.5 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-2xl sm:rounded-full shadow-xl border border-gray-200 dark:border-gray-700 transition-all duration-200 flex-wrap sm:flex-nowrap'>
        {/* Marker count badge */}
        <div className='flex items-center gap-2 px-3 py-1.5'>
          <div className='flex items-center gap-1.5'>
            <div className='w-5 h-5 rounded-full bg-orange-500 text-white text-xs font-semibold flex items-center justify-center'>
              {markerCount}
            </div>
            <span className='text-sm text-gray-600 dark:text-gray-300'>
              {markerCount === 1 ? t('marker') : t('markers')}
            </span>
          </div>
        </div>

        <div className='w-px h-5 sm:h-6 bg-gray-200 dark:bg-gray-700' />

        {/* Undo/Redo */}
        <div className='flex items-center gap-0.5 px-0.5 sm:px-1'>
          <SimpleTooltip title={t('Undo')} side='bottom'>
            <button
              onClick={onUndo}
              disabled={!canUndo || isGenerating}
              className='p-1.5 sm:p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200'
            >
              <ChevronLeftIcon className='h-4 w-4 sm:h-5 sm:w-5' />
            </button>
          </SimpleTooltip>
          <SimpleTooltip title={t('Redo')} side='bottom'>
            <button
              onClick={onRedo}
              disabled={!canRedo || isGenerating}
              className='p-1.5 sm:p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200'
            >
              <ChevronRightIcon className='h-4 w-4 sm:h-5 sm:w-5' />
            </button>
          </SimpleTooltip>
        </div>

        {/* Clear All */}
        <SimpleTooltip title={t('Clear All')} side='bottom'>
          <button
            onClick={onClearAll}
            disabled={markerCount === 0 || isGenerating}
            className='p-1.5 sm:p-2 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200'
          >
            <TrashIcon className='h-4 w-4 sm:h-5 sm:w-5' />
          </button>
        </SimpleTooltip>

        {/* Cancel/Done - Only show when not embedded */}
        {!embedded && (
          <>
            <div className='w-px h-5 sm:h-6 bg-gray-200 dark:bg-gray-700' />

            <div className='flex items-center gap-1 sm:gap-2 pl-1 sm:pl-2 pr-0.5 sm:pr-1'>
              <button
                onClick={onCancel}
                disabled={isGenerating}
                className='px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50'
              >
                {t('Cancel')}
              </button>
              <button
                onClick={onDone}
                disabled={!hasValidAnnotations || isGenerating}
                className='px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md hover:shadow-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 sm:gap-2'
              >
                {isGenerating && (
                  <Spinner className='animate-spin-fast h-3 w-3 sm:h-4 sm:w-4 text-white' />
                )}
                {isGenerating ? t('Generating...') : t('Generate')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default memo(AnnotationToolbar);
